import { z } from 'zod';

/**
 * Video preferences for generation
 */
/**
 * Supported audio formats for Remotion
 */
export const SUPPORTED_AUDIO_FORMATS = ['mp3', 'wav', 'm4a', 'aac', 'ogg'] as const;

export const VideoPreferencesSchema = z.object({
  fps: z.number().int().min(24).max(60).default(30),
  width: z.number().int().min(100).max(4096).default(1920),
  height: z.number().int().min(100).max(4096).default(1080),
  durationInSeconds: z.number().min(1).max(300).default(10),
  audioUrl: z.string().url().optional().refine(
    (url) => {
      if (!url) return true;
      const ext = url.split('.').pop()?.toLowerCase();
      return SUPPORTED_AUDIO_FORMATS.includes(ext as typeof SUPPORTED_AUDIO_FORMATS[number]);
    },
    { message: 'Desteklenen formatlar: mp3, wav, m4a, aac, ogg' }
  ),
});

export type VideoPreferences = z.infer<typeof VideoPreferencesSchema>;

/**
 * Generation request schema
 */
export const GenerateRequestSchema = z.object({
  prompt: z
    .string()
    .min(10, "Prompt must be at least 10 characters")
    .max(30000, "Prompt must be less than 30000 characters"),
  projectId: z.string().uuid().optional(),
  preferences: VideoPreferencesSchema.optional().default({}),
});

export type GenerateRequest = z.infer<typeof GenerateRequestSchema>;

/**
 * Citation from RAG system
 */
export const CitationSchema = z.object({
  source: z.string(),
  relevance: z.number().min(0).max(1),
  snippet: z.string(),
});

export type Citation = z.infer<typeof CitationSchema>;

/**
 * Generation metadata
 */
export const GenerationMetadataSchema = z.object({
  tokensUsed: z.number().int().nonnegative(),
  generationTimeMs: z.number().int().nonnegative(),
  modelVersion: z.string(),
  citations: z.array(CitationSchema),
});

export type GenerationMetadata = z.infer<typeof GenerationMetadataSchema>;

/**
 * Validation error
 */
export const ValidationErrorSchema = z.object({
  type: z.enum(['syntax', 'type', 'lint', 'runtime']),
  message: z.string(),
  line: z.number().int().optional(),
  column: z.number().int().optional(),
});

export type ValidationError = z.infer<typeof ValidationErrorSchema>;

/**
 * Generation result data
 */
export const GenerationDataSchema = z.object({
  code: z.string(),
  componentName: z.string(),
  explanation: z.string(),
  previewUrl: z.string().url().nullable(),
  isValid: z.boolean(),
  validationErrors: z.array(ValidationErrorSchema),
  metadata: GenerationMetadataSchema,
});

export type GenerationData = z.infer<typeof GenerationDataSchema>;

/**
 * Full generation response
 */
export const GenerateResponseSchema = z.object({
  success: z.boolean(),
  data: GenerationDataSchema.nullable(),
  error: z
    .object({
      code: z.string(),
      message: z.string(),
      details: z.record(z.unknown()).optional(),
    })
    .nullable(),
});

export type GenerateResponse = z.infer<typeof GenerateResponseSchema>;

/**
 * Streaming generation event types
 */
export const GenerationEventSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('started'),
    timestamp: z.string().datetime(),
  }),
  z.object({
    type: z.literal('context_retrieved'),
    citationsCount: z.number(),
    timestamp: z.string().datetime(),
  }),
  z.object({
    type: z.literal('generating'),
    progress: z.number().min(0).max(100),
    timestamp: z.string().datetime(),
  }),
  z.object({
    type: z.literal('validating'),
    timestamp: z.string().datetime(),
  }),
  z.object({
    type: z.literal('completed'),
    data: GenerationDataSchema,
    timestamp: z.string().datetime(),
  }),
  z.object({
    type: z.literal('error'),
    error: z.object({
      code: z.string(),
      message: z.string(),
    }),
    timestamp: z.string().datetime(),
  }),
]);

export type GenerationEvent = z.infer<typeof GenerationEventSchema>;
